<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showExtraBtn="true"
                :showAddNew="false"
                title="Task Calendar"
                extraBtnTitle="List View"
                @onClickExtraBtn="navigateToListPage"
            />
        </div>

        <div class="px-2 pb-2">
          <div class="row">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Task Role</label>
              <v-select
                  placeholder="Select Task Role"
                  :options="taskCalendarFilterList"
                  v-model="taskCalendarType"
                  label="name"
                  :reduce="name => name.value"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Task Status</label>
              <v-select
                  placeholder="Select Task Status"
                  :options="taskCalendarStatusList"
                  v-model="taskCalendarStatus"
                  label="name"
                  :reduce="name => name.value"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3" v-if="isUserAccess">
              <label for="colFormLabel" class="col-form-label">Team Member</label>
              <AsyncSelect
                  placeholder="Team Member"
                  v-model="contactProfile"
                  :api-service="fetchContactProfiles"
                  :additional-query="{roles: 'employee', status: 'active'}"
                  label="name"
              />
            </div>

            <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
              <button
                  style="min-width: 64px;"
                  @click="onEventSubmitted"
                  class="btn btn-primary btn-block waves-effect waves-float waves-light"
              >GO
              </button>
            </div>
          </div>
        </div>

        <div class="app-calendar overflow-hidden border">
            <div class="row no-gutters">
                <div class="col position-relative">
                    <div class="card shadow-none border-0 mb-0 rounded-0">
                        <div class="card-body pb-0" v-show="!loading">
                          <CalendarComponent
                              :defaultView="'timeGridWeek'"
                              :calendarOptions="calendarOptions"
                              ref="calendarRef"
                          />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Loader v-if="loading"/>
        <TaskEventScheduler
            v-if="store.state.isModalOpen"
            :startDate="startDate"
            :endDate="endDate"
            :editData="singleActivity"
            :taskStatus="taskStatus"
            :workStatus="workStatus"
            :markAsDoneShow="markAsDoneShow"
            :isCompleted="isCompleted"
            @submitted="onEventSubmitted"
        />
    </div>
</template>

<script setup>
import {ref, reactive, inject, onMounted, nextTick} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import CalendarComponent from '@/components/atom/calendar/CalendarComponent.vue';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import { getCurrentDateTime, taskStatusColors } from '@/services/utils/global.js';
import handleActivity from "@/services/modules/procurement/activity";
import handleChartAccount from '@/services/modules/chartAccount'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from '@/services/modules/contact'
import useDate from '@/services/utils/day'

const { fetchTaskActivityLog, fetchSingleTaskActivityLog, updateDragTaskActivityLog } = handleActivity()
const { fetchHome } = handleChartAccount()
const { fetchContactProfiles } = handleContact()
const { formatDateTime, formatToDate } = useDate()

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(false);
const currentEvents = ref([]);
const singleActivity = ref(null);
const isUserAccess = ref(false);
const taskStatus = ref({});
const workStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const calendarRef = ref(null);
const taskCalendarFilterList = ref([]);
const taskCalendarStatusList = ref([]);
const taskCalendarType = ref('');
const taskCalendarStatus = ref('');
const startDate = ref('');
const endDate = ref('');
const contactProfile = ref('');

const calendarOptions = reactive({
  initialEvents: [],
  editable: true,
  selectable: true,
  selectMirror: true,
  dayMaxEvents: true,
  weekends: true,
  select: handleDateSelect,
  eventClick: handleEventClick,
  eventsSet: handleEvents,
  events: async function(info, successCallback, failureCallback) {
    try {
      let selectedMonthStart = info.startStr.substring(0, 10);
      let selectedMonthEnd = info.endStr.substring(0, 10);

      let query = getQuery();
      if (selectedMonthStart) query += '&start_date=' + selectedMonthStart;
      if (selectedMonthEnd) query += '&end_date=' + selectedMonthEnd;

      const res = await fetchTaskActivityLog(query);

      if (!res.status) {
        successCallback([]);
        return;
      }

      isUserAccess.value = res.user_filter_access

      const events = res.data.map(item => {
        let color = taskStatusColors[item.status] || { bg: '#6b7280', border: '#4b5563', text: '#ffffff' };

        return {
          id: item.id,
          status: item.status,
          title: item.short_description,
          start: getCurrentDateTime(item.start_time),
          end: item.end_time ? getCurrentDateTime(item.end_time) : null,
          backgroundColor: color.bg,
          borderColor: color.border,
          textColor: color.text,
        };
      });

      successCallback(events);
    } catch (error) {
      failureCallback(error);
    }
  },
  eventDrop: async function(info) {
    await updateTaskDateTime(info);
  },
  eventResize: async function(info) {
    await updateTaskDateTime(info);
  }
});

async function updateTaskDateTime(info) {
  const event = info.event;

  const payload = {
    id: event.id,
    start_time: event.start ? formatDateTime(event.start.toISOString()) : null,
    end_time: event.end ? formatDateTime(event.end.toISOString()) : null,
    date: event.start ? formatToDate(event.start.toISOString()) : null,
    company_id: route.params.companyId
  };

  try {
    const res = await updateDragTaskActivityLog(payload.id, payload);
    if (!res.status) {
      info.revert();
      return showError(res.message || "Failed to update task.");
    }
    showSuccess("Task updated successfully!");
  } catch (err) {
    info.revert();
    console.error(err);
    showError("Something went wrong while updating task.");
  }
}

const onEventSubmitted = () => {
  let calendarApi = null;
  if (
      calendarRef.value &&
      typeof calendarRef.value.getApi === 'function'
  ) {
    calendarApi = calendarRef.value.getApi();
  }

  if (calendarApi) {
    calendarApi.refetchEvents();
  }
};

const getQuery = () => {
  const companyId = route.params.companyId;
  const isMySchedule = route.query.from === 'my-visit-schedule';
  let query = `?company_id=${companyId}&route_name=task-calendar`;
  if (isMySchedule) query += `&my_plan=1`;
  if (taskCalendarType.value) query += `&task_calendar_type=${taskCalendarType.value}`;
  if (taskCalendarStatus.value) query += `&status=${taskCalendarStatus.value}`;
  if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`;
  return query;
}

function handleDateSelect(selectInfo) {
  const selectedDate = new Date(selectInfo.start);
  const now = new Date();

  if (selectInfo.allDay){
    selectedDate.setHours(now.getHours());
    selectedDate.setMinutes(now.getMinutes());
  }

  const selectedEnd = new Date(selectedDate);
  selectedEnd.setHours(selectedDate.getHours() + 1);

  const formatDateTime = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  startDate.value = formatDateTime(selectedDate);
  endDate.value = formatDateTime(selectedEnd);
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};
  markAsDoneShow.value = false;
  isCompleted.value = false;
  store.state.isModalOpen = true;
}

function navigateToListPage() {
  router.push({name: `task-management`, params: route.params, query: route.query});
}

async function handleEventClick(clickInfo) {
  const activityId = clickInfo.event.id;
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};
  const calendarApi = calendarRef.value && calendarRef.value.getApi();

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    if (res.work_status){
      workStatus.value = res.work_status;
    }
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }

    if (calendarApi && response.start_time) {
      const dateObj = new Date(response.start_time.replace(" ", "T"));
      await nextTick();
      calendarApi.gotoDate(dateObj);
    }

    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

function handleEvents(events) {
  currentEvents.value = events;
}

onMounted(() => {
  Promise.all([
    fetchHome().then(res => {
      if (!res.data) {
        return;
      }
      if (res.data && res.data.task_calendar_filter){
        taskCalendarFilterList.value = res.data.task_calendar_filter;
      }
      if (res.data && res.data.task_calendar_status){
        taskCalendarStatusList.value = res.data.task_calendar_status;
      }
    })
  ])

})

</script>
